import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { SettingsProvider } from "@/lib/settings";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectView from "@/pages/ProjectView";
import MyWork from "@/pages/MyWork";
import IssuesAll from "@/pages/IssuesAll";
import IssueDetailPage from "@/pages/IssueDetailPage";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import Automations from "@/pages/Automations";
import JiraImport from "@/pages/JiraImport";
import Roadmap from "@/pages/Roadmap";
import Pages from "@/pages/Pages";
import Teams from "@/pages/Teams";
import AuditLog from "@/pages/AuditLog";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectId" element={<ProjectView />} />
        <Route path="projects/:projectId/:tab" element={<ProjectView />} />
        <Route path="my-work" element={<MyWork />} />
        <Route path="issues" element={<IssuesAll />} />
        <Route path="issues/:issueKey" element={<IssueDetailPage />} />
        <Route path="reports" element={<Reports />} />
        <Route path="roadmap" element={<Roadmap />} />
        <Route path="pages" element={<Pages />} />
        <Route path="teams" element={<Teams />} />
        <Route path="audit" element={<AdminOnly><AuditLog /></AdminOnly>} />
        <Route path="automations" element={<Automations />} />
        <Route path="import" element={<JiraImport />} />
        <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="bottom-right" theme="system" />
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
